/**
 * UsPersonVerificationDialog — ITAR §120.15 US Person verification workflow.
 *
 * A security officer (typically admin with verification authority) reviews
 * a user's identification document and records the verification in the
 * audit trail. This is a regulated compliance workflow — not just a checkbox.
 *
 * Steps:
 *   1. Document type selection
 *   2. Document details entry (number, expiry, notes)
 *   3. Attestation + officer signature
 *
 * Output: creates a US Person verification record on the user + hash-chained
 * audit event + optional re-verification reminder if document has expiry.
 */

import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  UserCheck, Check, ShieldAlert, AlertCircle, ChevronLeft, ChevronRight,
  Calendar, FileText, Lock, User as UserIcon, Loader2,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  type User, type UsPersonDocumentType,
} from '@/lib/mock-users-data';
import { UserAvatar } from './user-badges';
import { apiClient } from '@/lib/api-client';

export interface UsPersonVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User | null;
  onVerified: (user: User, payload: VerificationPayload) => void;
}

export interface VerificationPayload {
  userId: string;
  documentType: UsPersonDocumentType;
  documentNumber: string;        // hashed in production
  documentExpiresAt: string | null;
  verifyingOfficerName: string;
  officerEmployeeId: string;
  notes: string;
  attestation: boolean;
}

type Step = 'document' | 'details' | 'attest';

const STEPS: Array<{ id: Step; label: string; icon: any; description: string }> = [
  { id: 'document', label: 'Document Type',   icon: FileText, description: 'Select verification document' },
  { id: 'details',  label: 'Document Details', icon: UserIcon, description: 'Enter document information' },
  { id: 'attest',   label: 'Attestation',      icon: Lock,     description: 'Sign the verification record' },
];

const DOC_TYPES: Array<{ id: UsPersonDocumentType; label: string; hasExpiry: boolean; description: string }> = [
  { id: 'us_passport',             label: 'US Passport',             hasExpiry: true,  description: 'Current or expired within 5 years' },
  { id: 'drivers_license_ssn',     label: "Driver's License + SSN",  hasExpiry: true,  description: 'State-issued DL paired with SSN card' },
  { id: 'permanent_resident_card', label: 'Permanent Resident Card', hasExpiry: true,  description: 'Green card (I-551)' },
  { id: 'i_551',                   label: 'I-551 Stamp',             hasExpiry: true,  description: 'Temporary I-551 stamp in passport' },
  { id: 'birth_certificate',       label: 'US Birth Certificate',    hasExpiry: false, description: 'Certified copy, state-issued' },
  { id: 'naturalization',          label: 'Naturalization Cert.',    hasExpiry: false, description: 'Certificate of Naturalization (N-550/N-570)' },
];

export function UsPersonVerificationDialog({
  open, onOpenChange, user, onVerified,
}: UsPersonVerificationDialogProps) {
  // USR-F1-T1 — after a successful PATCH the screens RE-READ the server.
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('document');
  const [documentType, setDocumentType] = useState<UsPersonDocumentType>('us_passport');
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentExpiresAt, setDocumentExpiresAt] = useState('');
  const [officerName, setOfficerName] = useState('');
  const [officerEmployeeId, setOfficerEmployeeId] = useState('');
  const [notes, setNotes] = useState('');
  const [attestation, setAttestation] = useState(false);
  // Bug #654 — actually submit to the backend instead of fake success.
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('document');
    setDocumentType('us_passport');
    setDocumentNumber('');
    setDocumentExpiresAt('');
    setOfficerName('');
    setOfficerEmployeeId('');
    setNotes('');
    setAttestation(false);
    setSubmitting(false);
  }, [open]);

  if (!user) return null;

  const currentStepIndex = STEPS.findIndex(s => s.id === step);
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === STEPS.length - 1;
  const doc = DOC_TYPES.find(d => d.id === documentType)!;

  const canProceed = (() => {
    if (step === 'document') return true;
    if (step === 'details') {
      if (!documentNumber.trim() || documentNumber.length < 4) return false;
      if (doc.hasExpiry && !documentExpiresAt) return false;
      return true;
    }
    if (step === 'attest') {
      return officerName.trim().length > 0
        && officerEmployeeId.trim().length > 0
        && attestation;
    }
    return false;
  })();

  const handleNext = async () => {
    if (!canProceed || submitting) return;
    if (!isLast) {
      setStep(STEPS[currentStepIndex + 1].id);
      return;
    }

    // Bug #654 fix — call the real backend PATCH /users/:id/identity-fields
    // endpoint. This sets usPersonVerified=true, usPersonVerifiedAt, and
    // (when the document has expiry) usPersonReverifyDue. The server-side
    // handler hash-chains a clearance.us_person.verified audit event,
    // enforces separation-of-duties (an admin cannot verify themselves),
    // and enforces the framework-active gate. Pre-fix the dialog only
    // emitted a toast + onVerified callback with no API call.
    setSubmitting(true);
    const expiresIso = doc.hasExpiry && documentExpiresAt
      ? new Date(documentExpiresAt).toISOString()
      : null;
    try {
      await apiClient.patch(`/users/${user.id}/identity-fields`, {
        usPersonVerified: true,
        usPersonVerifiedAt: new Date().toISOString(),
        usPersonReverifyDue: expiresIso,
      });
      // USR-F1-T1 (AC-11) — the roster (`useUsers`), the detail page
      // (`useUser`) and `useCurrentUser` all sit under the ['users'] key:
      // invalidate the family so every screen re-reads the server's
      // identity answer. Nothing patches a user object locally.
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onVerified(user, {
        userId: user.id,
        documentType,
        documentNumber,
        documentExpiresAt: expiresIso,
        verifyingOfficerName: officerName,
        officerEmployeeId,
        notes,
        attestation,
      });
      toast.success(`${user.fullName} verified as US Person`, {
        description: `Verified by ${officerName}. Audit event recorded.${
          doc.hasExpiry ? ` Re-verification due ${new Date(documentExpiresAt).toLocaleDateString()}.` : ''
        }`,
      });
      onOpenChange(false);
    } catch (err: any) {
      // api-client attaches the parsed error envelope on `err.data`
      // (and the formatted text on `err.message`) — match the repo
      // convention so FRAMEWORK_NOT_ACTIVE surfaces, not UNKNOWN_ERROR.
      const code = err?.data?.error?.code ?? err?.body?.error?.code ?? 'UNKNOWN_ERROR';
      const message = err?.data?.error?.message ?? err?.message ?? 'Verification failed';
      toast.error('US Person verification failed', { description: `${code}: ${message}` });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (isFirst) return;
    setStep(STEPS[currentStepIndex - 1].id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-primary" />
            US Person verification
          </DialogTitle>
          <DialogDescription>
            ITAR §120.15 verification workflow for <strong>{user.fullName}</strong>.
            The verifying officer must review a valid US Person document before submitting.
          </DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="px-6 py-3 border-b bg-muted/30">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const done = idx < currentStepIndex;
              const active = idx === currentStepIndex;
              return (
                <div key={s.id} className="flex items-center gap-2 flex-1">
                  <div className={cn(
                    'flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors min-w-0',
                    active && 'bg-primary text-primary-foreground',
                    done && 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
                    !active && !done && 'text-muted-foreground',
                  )}>
                    {done ? <Check className="h-3.5 w-3.5 shrink-0" /> : <Icon className="h-3.5 w-3.5 shrink-0" />}
                    <span className="truncate">{s.label}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={cn('flex-1 h-[1px]', done ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-border')} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-5 space-y-5">
            {/* User banner */}
            <div className="rounded-md border bg-muted/20 p-3 flex items-center gap-3">
              <UserAvatar name={user.fullName} color={user.avatarColor} size="md" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{user.fullName}</div>
                <div className="text-[11px] text-muted-foreground">
                  {user.position} · {user.email} · {user.employeeId}
                </div>
              </div>
              {user.identity?.usPersonVerified === true && (
                <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                  Previously verified
                </Badge>
              )}
            </div>

            {step === 'document' && <DocumentStep documentType={documentType} setDocumentType={setDocumentType} />}
            {step === 'details' && (
              <DetailsStep
                doc={doc}
                documentNumber={documentNumber}
                setDocumentNumber={setDocumentNumber}
                documentExpiresAt={documentExpiresAt}
                setDocumentExpiresAt={setDocumentExpiresAt}
              />
            )}
            {step === 'attest' && (
              <AttestStep
                user={user}
                doc={doc}
                officerName={officerName}
                setOfficerName={setOfficerName}
                officerEmployeeId={officerEmployeeId}
                setOfficerEmployeeId={setOfficerEmployeeId}
                notes={notes}
                setNotes={setNotes}
                attestation={attestation}
                setAttestation={setAttestation}
              />
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-muted-foreground">
              Step {currentStepIndex + 1} of {STEPS.length} — {STEPS[currentStepIndex].description}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              {!isFirst && (
                <Button variant="outline" onClick={handleBack}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              <Button onClick={handleNext} disabled={!canProceed || submitting}>
                {isLast ? (
                  submitting ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Submitting...</>
                  ) : (
                    <><UserCheck className="h-4 w-4 mr-1" /> Submit verification</>
                  )
                ) : (
                  <>Next <ChevronRight className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Step 1: Document Type ──────────────────────────────────────────────────

function DocumentStep({
  documentType, setDocumentType,
}: {
  documentType: UsPersonDocumentType;
  setDocumentType: (v: UsPersonDocumentType) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold mb-1">Select verification document</h4>
        <p className="text-[11px] text-muted-foreground">
          The document must be current and in the individual's legal name. You should have the physical or digitally-verified document in front of you during this process.
        </p>
      </div>

      <RadioGroup
        value={documentType}
        onValueChange={(v) => setDocumentType(v as UsPersonDocumentType)}
        className="space-y-2"
      >
        {DOC_TYPES.map(d => (
          <label
            key={d.id}
            className={cn(
              'flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors',
              documentType === d.id ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted/50',
            )}
          >
            <RadioGroupItem value={d.id} className="mt-0.5" />
            <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium">{d.label}</span>
                {d.hasExpiry && (
                  <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 gap-1">
                    <Calendar className="h-2.5 w-2.5" />
                    Has expiry
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{d.description}</p>
            </div>
          </label>
        ))}
      </RadioGroup>

      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
        <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800 dark:text-amber-300">
          This verification is a legal record. Only proceed if you have actually examined an original or high-confidence electronic copy of the document. False attestation is a violation of ITAR §120.15.
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Document Details ───────────────────────────────────────────────

function DetailsStep({
  doc,
  documentNumber, setDocumentNumber,
  documentExpiresAt, setDocumentExpiresAt,
}: {
  doc: typeof DOC_TYPES[0];
  documentNumber: string;
  setDocumentNumber: (v: string) => void;
  documentExpiresAt: string;
  setDocumentExpiresAt: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold mb-1">{doc.label} details</h4>
        <p className="text-[11px] text-muted-foreground">
          Enter the document reference information. Document numbers are hashed at rest; never stored in plaintext.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">
          Document number / identifier <span className="text-red-500">*</span>
        </Label>
        <Input
          value={documentNumber}
          onChange={(e) => setDocumentNumber(e.target.value)}
          placeholder={
            doc.id === 'us_passport' ? 'e.g., 123456789' :
            doc.id === 'drivers_license_ssn' ? 'DL number' :
            doc.id === 'permanent_resident_card' ? 'A-number (e.g., A12345678)' :
            'Document reference'
          }
          className="h-9 text-xs font-mono"
        />
        <p className="text-[10px] text-muted-foreground">
          Minimum 4 characters. Will be hashed with SHA-256 before storage.
        </p>
      </div>

      {doc.hasExpiry && (
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Document expiration date <span className="text-red-500">*</span>
          </Label>
          <Input
            type="date"
            value={documentExpiresAt}
            onChange={(e) => setDocumentExpiresAt(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
            className="h-9 text-xs w-48"
          />
          <p className="text-[10px] text-muted-foreground">
            User's verification will expire at this date. A reminder will fire 90 days before expiry.
          </p>
        </div>
      )}

      {doc.hasExpiry && documentExpiresAt && new Date(documentExpiresAt).getTime() < Date.now() && (
        <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 flex items-start gap-2">
          <ShieldAlert className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div className="text-xs text-red-800 dark:text-red-300">
            <strong>Document is expired.</strong> You cannot verify using an expired document. Please request a current one.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 3: Attestation ────────────────────────────────────────────────────

function AttestStep({
  user, doc,
  officerName, setOfficerName,
  officerEmployeeId, setOfficerEmployeeId,
  notes, setNotes,
  attestation, setAttestation,
}: {
  user: User;
  doc: typeof DOC_TYPES[0];
  officerName: string; setOfficerName: (v: string) => void;
  officerEmployeeId: string; setOfficerEmployeeId: (v: string) => void;
  notes: string; setNotes: (v: string) => void;
  attestation: boolean; setAttestation: (v: boolean) => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold mb-1">Officer attestation</h4>
        <p className="text-[11px] text-muted-foreground">
          Your name and employee ID will be recorded in the audit trail as the verifying officer for this record.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Officer name <span className="text-red-500">*</span>
          </Label>
          <Input
            value={officerName}
            onChange={(e) => setOfficerName(e.target.value)}
            placeholder="e.g., Mike Rodriguez"
            className="h-9 text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold">
            Employee ID <span className="text-red-500">*</span>
          </Label>
          <Input
            value={officerEmployeeId}
            onChange={(e) => setOfficerEmployeeId(e.target.value)}
            placeholder="EMP-0000"
            className="h-9 text-xs font-mono"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-semibold">
          Notes <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any relevant context or caveats (e.g., document condition, prior verification history)"
          rows={3}
          className="text-xs"
        />
      </div>

      <Separator />

      <div className="rounded-lg border-2 border-amber-300 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-3">
        <div className="flex items-start gap-2">
          <Lock className="h-4 w-4 text-amber-700 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Legal attestation required</p>
            <p className="text-amber-800 dark:text-amber-300 mt-1">
              By submitting this verification for {user.fullName}, I attest that:
            </p>
          </div>
        </div>
        <ul className="space-y-1 text-[11px] text-amber-900 dark:text-amber-200 list-disc pl-6">
          <li>I have personally reviewed the {doc.label} referenced above.</li>
          <li>The document is authentic, unaltered, and in {user.fullName}'s legal name.</li>
          <li>I understand this record is part of an auditable compliance trail under ITAR §120.15.</li>
          <li>I am authorized by my organization to perform US Person verifications.</li>
        </ul>
        <label className="flex items-start gap-2 cursor-pointer pt-2 border-t border-amber-200 dark:border-amber-900">
          <Checkbox
            checked={attestation}
            onCheckedChange={(c) => setAttestation(c === true)}
            className="mt-0.5"
          />
          <span className="text-xs font-medium text-amber-900 dark:text-amber-200">
            I attest to the above and submit this US Person verification for {user.fullName}.
          </span>
        </label>
      </div>
    </div>
  );
}
